import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Bar,
  CartesianGrid,
  Cell,
  ComposedChart,
  Line,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip as ChartTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  AlertTriangle,
  ArrowDown,
  ArrowUp,
  DollarSign,
  Download,
  Eye,
  MousePointerClick,
  Percent,
  Plug,
  Target,
  TrendingUp,
  X,
} from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card } from '@/components/ui/Card';
import { StatCard, type StatCardTone } from '@/components/ui/StatCard';
import { Select } from '@/components/ui/Select';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Spinner } from '@/components/ui/Spinner';
import { EmptyState } from '@/components/ui/EmptyState';
import { DateRangePicker } from '@/components/ui/DateRangePicker';
import { MultiSelectDropdown } from '@/components/ui/MultiSelectDropdown';
import { cn } from '@/lib/utils';
import { usePageContext } from '@/hooks/usePageContext';
import { defuseFormula } from '@/lib/csv';
import { downloadCsv } from '@/lib/orderExport';
import { getErrorCode, getErrorMessage } from '@/services/apiClient';
import {
  getAdsReport,
  type AdsCampaignRow,
  type AdsMetrics,
  type AdsProvider,
  type AdsReport,
} from '@/services/adsReport.service';
import {
  RANGE_OPTIONS,
  bucketFor,
  bucketKey,
  compact,
  delta,
  fromIso,
  isoDate,
  money,
  num,
  pct,
  resolveRange,
  shortDate,
  type RangeKey,
} from './adsReportFormat';
import { demoAdsReport } from './demoAdsReport';

export interface AdsProviderDashboardProps {
  provider: AdsProvider;
  title: string;
  subtitle: string;
  icon: ReactNode;
  /** Chart and accent colours for this provider. */
  colors: { primary: string; secondary: string; palette: string[] };
  tone: StatCardTone;
}

type StatusFilter = 'all' | 'active' | 'paused';
type ChartMetric = 'spend' | 'clicks' | 'impressions' | 'conversions';
type SortKey = 'name' | 'spend' | 'impressions' | 'clicks' | 'ctr' | 'cpc' | 'conversions' | 'cpa' | 'roas';

const CHART_METRICS: { value: ChartMetric; label: string }[] = [
  { value: 'spend', label: 'Spend' },
  { value: 'clicks', label: 'Clicks' },
  { value: 'impressions', label: 'Impressions' },
  { value: 'conversions', label: 'Conversions' },
];

const STATUS_OPTIONS: { value: StatusFilter; label: string }[] = [
  { value: 'all', label: 'All statuses' },
  { value: 'active', label: 'Active' },
  { value: 'paused', label: 'Paused' },
];

const isActive = (s: string) => s === 'active' || s === 'enabled';
const isPaused = (s: string) => s === 'paused';

function statusBadge(status: string) {
  if (isActive(status)) return <Badge variant="success" dot size="sm">Active</Badge>;
  if (isPaused(status)) return <Badge variant="warning" dot size="sm">Paused</Badge>;
  if (status === 'removed') return <Badge variant="neutral" size="sm">Removed</Badge>;
  return <Badge variant="neutral" size="sm">{status === 'unknown' ? '—' : status}</Badge>;
}

/**
 * One ads platform's dashboard: date range, campaign and status filters,
 * KPI cards against the previous period, a daily chart, the campaign table,
 * spend share and conversions by type. Filters live in the URL so a view can
 * be bookmarked or shared.
 */
export function AdsProviderDashboard({ provider, title, subtitle, icon, colors, tone }: AdsProviderDashboardProps) {
  const updateAIContext = usePageContext(title);
  const [params, setParams] = useSearchParams();

  // ─── Filters (URL-backed) ────────────────────────────────────────────────
  const rangeKey = (RANGE_OPTIONS.some((o) => o.value === params.get('range')) ? params.get('range') : '30d') as RangeKey;
  const preset = rangeKey === 'custom' ? null : resolveRange(rangeKey);
  const start = preset?.start ?? params.get('start') ?? resolveRange('30d').start;
  const end = preset?.end ?? params.get('end') ?? resolveRange('30d').end;
  const selectedIds = useMemo(() => (params.get('campaigns') ?? '').split(',').filter(Boolean), [params]);
  const status = (params.get('status') as StatusFilter) || 'all';
  const demo = import.meta.env.DEV && params.get('demo') === '1';

  const patch = (next: Record<string, string | null>) => {
    const p = new URLSearchParams(params);
    for (const [k, v] of Object.entries(next)) (v === null || v === '' ? p.delete(k) : p.set(k, v));
    setParams(p, { replace: true });
  };

  // All campaigns of the range, kept from the last answer so the status
  // filter can turn into campaign ids without a second request. Tagged with
  // the range they came from: another range's campaigns cannot answer "which
  // campaigns are Paused" for this one (Tom on #1255 — Status Paused, then
  // Last 12 months, kept the 30-day list, matched nothing, and so never
  // requested the new range at all).
  const rangeTag = `${start}|${end}`;
  const [known, setKnown] = useState<{ range: string; rows: AdsCampaignRow[] }>({ range: '', rows: [] });
  const knownCampaigns = useMemo(() => (known.range === rangeTag ? known.rows : []), [known, rangeTag]);
  // A status filter on a range whose campaigns we have not seen yet: fetch the
  // range unfiltered first to learn them, and show a spinner meanwhile rather
  // than the unfiltered figures.
  const resolvingStatus = status !== 'all' && known.range !== rangeTag;

  const statusIds = useMemo(() => {
    if (status === 'all' || resolvingStatus) return null;
    return knownCampaigns.filter((c) => (status === 'active' ? isActive(c.status) : isPaused(c.status))).map((c) => c.id);
  }, [status, knownCampaigns, resolvingStatus]);

  const effectiveIds = useMemo(() => {
    if (!statusIds) return selectedIds;
    if (!selectedIds.length) return statusIds;
    return selectedIds.filter((id) => statusIds.includes(id));
  }, [selectedIds, statusIds]);
  // A status with no matching campaign must show nothing, not everything.
  const nothingMatches = status !== 'all' && knownCampaigns.length > 0 && effectiveIds.length === 0;

  const { data, error, isError, isLoading, isFetching, isPlaceholderData, refetch } = useQuery<AdsReport>({
    queryKey: ['marketing', 'ads-report', provider, start, end, effectiveIds.join(','), demo],
    queryFn: () =>
      demo
        ? Promise.resolve(demoAdsReport(provider, start, end, effectiveIds))
        : getAdsReport(provider, { start, end, campaignIds: effectiveIds }),
    placeholderData: keepPreviousData,
    staleTime: 5 * 60_000,
    enabled: !nothingMatches,
  });

  // Only a real answer for THIS range: under keepPreviousData the placeholder
  // is the last range's report.
  useEffect(() => {
    if (data?.campaigns && !isPlaceholderData) setKnown({ range: rangeTag, rows: data.campaigns });
  }, [data?.campaigns, isPlaceholderData, rangeTag]);

  useEffect(() => {
    if (!data?.totals) return;
    updateAIContext({
      dataSummary: `${title} ${data.startDate}..${data.endDate}: spend ${data.totals.spend} ${data.currency ?? ''}, `
        + `${data.totals.conversions} conversions, ${data.campaigns?.length ?? 0} campaigns`,
    });
  }, [data, title, updateAIContext]);

  // A failed request must never fall through to the KPI grid: with no data it
  // would print $0.00 as if it were real, and under keepPreviousData the last
  // range's figures would sit under the new dates.
  const failed = isError && !nothingMatches;
  const rangeRejected = failed && getErrorCode(error) === 'INVALID_RANGE';
  const requestError = failed
    ? getErrorMessage(error, `${title} could not be loaded. Try again in a moment.`)
    : null;

  const currency = failed ? null : data?.currency ?? null;
  const totals = nothingMatches ? null : data?.totals ?? null;
  const prev = nothingMatches ? null : data?.previousTotals ?? null;

  // ─── Render ───────────────────────────────────────────────────────────────
  const header = (
    <PageHeader
      title={title}
      subtitle={
        <span className="flex items-center gap-1.5">
          {icon}
          {subtitle}
          {currency && <span className="text-slate-400">· {currency}</span>}
        </span>
      }
      actions={
        <div className="flex items-center gap-2">
          {isFetching && !isLoading && <Spinner className="h-4 w-4" />}
          <Link to="/marketing/ads">
            <Button variant="ghost" size="sm">Ads Center</Button>
          </Link>
          <Link to="/admin/marketing-integrations">
            <Button variant="ghost" size="sm">
              <Plug className="mr-1 h-4 w-4" />
              Manage
            </Button>
          </Link>
        </div>
      }
    />
  );

  const filters = (
    <Card className="mb-5">
      <div className="flex flex-wrap items-end gap-3">
        <div className="w-44">
          <p className="mb-1.5 text-xs font-medium text-slate-500">Date range</p>
          <Select
            options={RANGE_OPTIONS}
            value={rangeKey}
            onChange={(v) => {
              if (!v) return;
              if (v === 'custom') patch({ range: 'custom', start, end });
              else patch({ range: v, start: null, end: null });
            }}
            className="text-sm"
          />
        </div>
        {rangeKey === 'custom' && (
          // Sized by its two date fields rather than a fixed width they could
          // overflow (the Campaigns select drew over the End date at 1440px).
          <div className="w-full sm:w-auto sm:flex-none">
            <DateRangePicker
              className="sm:[&>*]:min-w-[11rem]"
              value={{ startDate: fromIso(start), endDate: fromIso(end) }}
              onChange={(r) => patch({
                start: r.startDate ? isoDate(r.startDate) : start,
                end: r.endDate ? isoDate(r.endDate) : end,
              })}
            />
          </div>
        )}
        <div className="w-64">
          <p className="mb-1.5 text-xs font-medium text-slate-500">Campaigns</p>
          <MultiSelectDropdown
            options={known.rows.map((c, i) => ({ id: i, label: c.name }))}
            selected={new Set(known.rows.flatMap((c, i) => (selectedIds.includes(c.id) ? [i] : [])))}
            onToggle={(i) => {
              const id = known.rows[i]?.id;
              if (!id) return;
              const next = selectedIds.includes(id) ? selectedIds.filter((x) => x !== id) : [...selectedIds, id];
              patch({ campaigns: next.join(',') });
            }}
            onSelectAll={(visible) => {
              const ids = (visible ?? known.rows.map((_, i) => i)).map((i) => known.rows[i].id);
              patch({ campaigns: Array.from(new Set([...selectedIds, ...ids])).join(',') });
            }}
            onClearAll={() => patch({ campaigns: null })}
            placeholder={selectedIds.length ? `${selectedIds.length} selected` : 'All campaigns'}
            searchable
          />
        </div>
        <div className="w-40">
          <p className="mb-1.5 text-xs font-medium text-slate-500">Status</p>
          <Select
            options={STATUS_OPTIONS}
            value={status}
            onChange={(v) => patch({ status: v && v !== 'all' ? v : null })}
            className="text-sm"
          />
        </div>
        <div className="ml-auto flex items-center gap-3 pb-2 text-xs text-slate-500">
          {data && !failed && (
            <span>
              {shortDate(data.startDate, true)} – {shortDate(data.endDate, true)}
              <span className="text-slate-400"> · vs {shortDate(data.previous.startDate, true)} – {shortDate(data.previous.endDate, true)}</span>
            </span>
          )}
          {(selectedIds.length > 0 || status !== 'all') && (
            <button
              type="button"
              className="inline-flex items-center gap-1 font-medium text-blue-600 hover:text-blue-700"
              onClick={() => patch({ campaigns: null, status: null })}
            >
              <X className="h-3.5 w-3.5" /> Clear filters
            </button>
          )}
        </div>
      </div>
    </Card>
  );

  const errorCard = (message: string, actions: ReactNode) => (
    <Card className="border-amber-200 bg-amber-50/60">
      <div className="flex items-start gap-3">
        <AlertTriangle className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-600" />
        <div className="min-w-0">
          <p className="font-medium text-amber-900">Couldn’t load {title}</p>
          <p className="mt-1 break-words text-sm text-amber-800">{message}</p>
          <div className="mt-3 flex flex-wrap gap-2">{actions}</div>
        </div>
      </div>
    </Card>
  );

  let body: ReactNode;
  if ((isLoading && !data) || (resolvingStatus && !isError)) {
    body = <div className="flex items-center justify-center py-24"><Spinner /></div>;
  } else if (requestError !== null) {
    body = errorCard(
      rangeRejected ? `${requestError}. Pick a shorter range above.` : requestError,
      rangeRejected ? (
        <Button size="sm" variant="secondary" onClick={() => patch({ range: '30d', start: null, end: null })}>
          Show the last 30 days
        </Button>
      ) : (
        <Button size="sm" variant="secondary" onClick={() => refetch()} disabled={isFetching}>
          {isFetching ? 'Trying…' : 'Try again'}
        </Button>
      ),
    );
  } else if (data && !data.connected) {
    body = (
      <Card>
        <EmptyState
          icon={<Plug className="h-8 w-8" />}
          title={`${title} is not connected`}
          description="Connect the ad account in Marketing Integrations to see campaigns, spend and conversions here."
          action={{ label: 'Connect', onClick: () => { window.location.href = '/admin/marketing-integrations'; } }}
        />
      </Card>
    );
  } else if (data?.error) {
    // The API already words this for clinic staff (BuildsAdsReports) and logs
    // the provider's own message.
    body = errorCard(
      data.error,
      <>
        <Button size="sm" variant="secondary" onClick={() => refetch()} disabled={isFetching}>
          {isFetching ? 'Trying…' : 'Try again'}
        </Button>
        <Link to="/admin/marketing-integrations"><Button size="sm" variant="ghost">Open Marketing settings</Button></Link>
      </>,
    );
  } else {
    body = (
      <>
        <KpiGrid totals={totals} prev={prev} currency={currency} tone={tone} />
        <div className="mt-5 grid grid-cols-1 gap-5 xl:grid-cols-3">
          <TrendCard data={data} currency={currency} colors={colors} className="xl:col-span-2" empty={nothingMatches} />
          <SpendShareCard
            campaigns={(data?.campaigns ?? []).filter((c) => !effectiveIds.length || effectiveIds.includes(c.id))}
            currency={currency}
            palette={colors.palette}
          />
        </div>
        <div className="mt-5 grid grid-cols-1 gap-5 xl:grid-cols-3">
          <CampaignTable
            campaigns={data?.campaigns ?? []}
            status={status}
            selectedIds={selectedIds}
            currency={currency}
            title={title}
            range={`${start}_${end}`}
            onToggle={(id) => {
              const next = selectedIds.includes(id) ? selectedIds.filter((x) => x !== id) : [...selectedIds, id];
              patch({ campaigns: next.join(',') });
            }}
            className="xl:col-span-2"
          />
          <ConversionTypesCard rows={nothingMatches ? [] : data?.conversionsByType ?? []} currency={currency} color={colors.primary} />
        </div>
      </>
    );
  }

  return (
    <div className="w-full min-w-0 px-6 pb-10 pt-6">
      {header}
      {filters}
      {body}
      {demo && <p className="mt-6 text-center text-xs text-slate-400">Demo data (local dev only)</p>}
    </div>
  );
}

// ─── KPI cards ────────────────────────────────────────────────────────────────

function KpiGrid({ totals, prev, currency, tone }: { totals: AdsMetrics | null; prev: AdsMetrics | null; currency: string | null; tone: StatCardTone }) {
  const t = totals;
  const card = (label: string, value: string, key: keyof AdsMetrics, iconNode: ReactNode, lowerIsBetter = false, tooltip?: string) => {
    const d = t && prev ? delta(t[key] as number | null, prev[key] as number | null, lowerIsBetter) : null;
    return (
      <StatCard
        key={label}
        label={label}
        value={value}
        icon={iconNode}
        tone={tone}
        tooltip={tooltip}
        subtitle={prev ? 'vs previous period' : undefined}
        {...(d ?? {})}
      />
    );
  };
  const showRoas = (t?.roas ?? null) !== null || (prev?.roas ?? null) !== null;
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-4">
      {card('Spend', money(t?.spend ?? 0, currency), 'spend', <DollarSign className="h-4 w-4" />)}
      {card('Impressions', num(t?.impressions ?? 0), 'impressions', <Eye className="h-4 w-4" />)}
      {card('Clicks', num(t?.clicks ?? 0), 'clicks', <MousePointerClick className="h-4 w-4" />)}
      {card('CTR', pct(t?.ctr), 'ctr', <Percent className="h-4 w-4" />, false, 'Clicks ÷ impressions')}
      {card('Conversions', num(t?.conversions ?? 0, (t?.conversions ?? 0) % 1 ? 1 : 0), 'conversions', <Target className="h-4 w-4" />)}
      {card('Cost / conversion', money(t?.cpa, currency), 'cpa', <Target className="h-4 w-4" />, true, 'Spend ÷ conversions')}
      {card('Avg. CPC', money(t?.cpc, currency), 'cpc', <MousePointerClick className="h-4 w-4" />, true, 'Spend ÷ clicks')}
      {showRoas
        ? card('ROAS', t?.roas !== null && t?.roas !== undefined ? `${t.roas.toFixed(2)}x` : '—', 'roas', <TrendingUp className="h-4 w-4" />, false, 'Conversion value ÷ spend')
        : card('CPM', money(t?.cpm, currency), 'cpm', <Eye className="h-4 w-4" />, true, 'Cost per 1,000 impressions')}
    </div>
  );
}

// ─── Trend chart ──────────────────────────────────────────────────────────────

function TrendCard({ data, currency, colors, className, empty }: {
  data: AdsReport | undefined; currency: string | null; colors: AdsProviderDashboardProps['colors']; className?: string; empty: boolean;
}) {
  const [metric, setMetric] = useState<ChartMetric>('spend');
  const daily = empty ? [] : data?.daily ?? [];
  const bucket = bucketFor(daily.length);

  const series = useMemo(() => {
    const map = new Map<string, { key: string; spend: number; clicks: number; impressions: number; conversions: number }>();
    for (const d of daily) {
      const k = bucketKey(d.date, bucket);
      const cur = map.get(k) ?? { key: k, spend: 0, clicks: 0, impressions: 0, conversions: 0 };
      cur.spend += d.spend; cur.clicks += d.clicks; cur.impressions += d.impressions; cur.conversions += d.conversions;
      map.set(k, cur);
    }
    return Array.from(map.values());
  }, [daily, bucket]);

  const fmt = (v: number) => (metric === 'spend' ? money(v, currency) : num(v, metric === 'conversions' && v % 1 ? 1 : 0));
  const hasData = series.some((s) => s.spend || s.clicks || s.impressions || s.conversions);

  return (
    <Card className={className}>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <div>
          <h3 className="text-sm font-semibold text-slate-900">Performance over time</h3>
          <p className="text-xs text-slate-500">
            {CHART_METRICS.find((m) => m.value === metric)?.label}
            {metric !== 'conversions' && ' (bars) and conversions (line)'} by {bucket}
          </p>
        </div>
        <div className="inline-flex rounded-lg border border-slate-200 p-0.5">
          {CHART_METRICS.map((m) => (
            <button
              key={m.value}
              type="button"
              onClick={() => setMetric(m.value)}
              className={cn(
                'rounded-md px-2.5 py-1 text-xs font-medium transition-colors',
                metric === m.value ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-100',
              )}
            >
              {m.label}
            </button>
          ))}
        </div>
      </div>
      {hasData ? (
        <div className="h-72">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={series} margin={{ top: 4, right: 8, bottom: 0, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
              <XAxis
                dataKey="key"
                tickFormatter={(k: string) => (bucket === 'month' ? fromIso(k)!.toLocaleDateString(undefined, { month: 'short', year: '2-digit' }) : shortDate(k))}
                tick={{ fontSize: 11, fill: '#64748b' }}
                tickLine={false}
                axisLine={false}
                minTickGap={16}
              />
              <YAxis yAxisId="left" tickFormatter={(v: number) => compact(v)} tick={{ fontSize: 11, fill: '#64748b' }} tickLine={false} axisLine={false} width={48} />
              {metric !== 'conversions' && (
                <YAxis yAxisId="right" orientation="right" tickFormatter={(v: number) => compact(v)} tick={{ fontSize: 11, fill: '#64748b' }} tickLine={false} axisLine={false} width={36} />
              )}
              <ChartTooltip
                cursor={{ fill: 'rgba(148,163,184,0.12)' }}
                contentStyle={{ borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 12 }}
                labelFormatter={(k: string) => (bucket === 'day' ? shortDate(k, true) : `${bucket === 'week' ? 'Week of' : ''} ${shortDate(k, true)}`)}
                formatter={(v: number, name: string) => [name === 'Conversions' ? num(v, v % 1 ? 1 : 0) : fmt(v), name]}
              />
              <Bar yAxisId="left" dataKey={metric} name={CHART_METRICS.find((m) => m.value === metric)?.label} fill={colors.primary} radius={[4, 4, 0, 0]} maxBarSize={36} />
              {metric !== 'conversions' && (
                <Line yAxisId="right" type="monotone" dataKey="conversions" name="Conversions" stroke={colors.secondary} strokeWidth={2} dot={false} />
              )}
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <div className="flex h-72 items-center justify-center text-sm text-slate-400">No activity in this range</div>
      )}
    </Card>
  );
}

// ─── Spend share ──────────────────────────────────────────────────────────────

function SpendShareCard({ campaigns, currency, palette }: { campaigns: AdsCampaignRow[]; currency: string | null; palette: string[] }) {
  const total = campaigns.reduce((s, c) => s + c.spend, 0);
  const top = campaigns.filter((c) => c.spend > 0).slice(0, 5);
  const rest = total - top.reduce((s, c) => s + c.spend, 0);
  const slices = [...top.map((c) => ({ name: c.name, value: c.spend })), ...(rest > 0.005 ? [{ name: 'Other campaigns', value: rest }] : [])];

  return (
    <Card>
      <h3 className="text-sm font-semibold text-slate-900">Spend by campaign</h3>
      <p className="mb-2 text-xs text-slate-500">Share of {money(total, currency)}</p>
      {slices.length ? (
        <>
          <div className="relative h-44">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={slices} dataKey="value" nameKey="name" innerRadius={52} outerRadius={78} paddingAngle={2} stroke="none">
                  {slices.map((_, i) => <Cell key={i} fill={palette[i % palette.length]} />)}
                </Pie>
                <ChartTooltip formatter={(v: number) => money(v, currency)} contentStyle={{ borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 12 }} />
              </PieChart>
            </ResponsiveContainer>
            <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
              <span className="text-lg font-semibold text-slate-900">{campaigns.filter((c) => c.spend > 0).length}</span>
              <span className="text-2xs uppercase tracking-wide text-slate-400">campaigns</span>
            </div>
          </div>
          <ul className="mt-3 space-y-1.5">
            {slices.map((s, i) => (
              <li key={s.name} className="flex items-center gap-2 text-xs">
                <span className="h-2.5 w-2.5 flex-shrink-0 rounded-sm" style={{ background: palette[i % palette.length] }} />
                <span className="min-w-0 flex-1 truncate text-slate-700" title={s.name}>{s.name}</span>
                <span className="tabular-nums text-slate-500">{total ? ((s.value / total) * 100).toFixed(0) : 0}%</span>
              </li>
            ))}
          </ul>
        </>
      ) : (
        <div className="flex h-44 items-center justify-center text-sm text-slate-400">No spend in this range</div>
      )}
    </Card>
  );
}

// ─── Campaign table ───────────────────────────────────────────────────────────

const COLUMNS: { key: SortKey; label: string; align?: 'right' }[] = [
  { key: 'name', label: 'Campaign' },
  { key: 'spend', label: 'Spend', align: 'right' },
  { key: 'impressions', label: 'Impr.', align: 'right' },
  { key: 'clicks', label: 'Clicks', align: 'right' },
  { key: 'ctr', label: 'CTR', align: 'right' },
  { key: 'cpc', label: 'CPC', align: 'right' },
  { key: 'conversions', label: 'Conv.', align: 'right' },
  { key: 'cpa', label: 'Cost / conv.', align: 'right' },
  { key: 'roas', label: 'ROAS', align: 'right' },
];

function CampaignTable({ campaigns, status, selectedIds, currency, title, range, onToggle, className }: {
  campaigns: AdsCampaignRow[]; status: StatusFilter; selectedIds: string[]; currency: string | null;
  title: string; range: string; onToggle: (id: string) => void; className?: string;
}) {
  const [sort, setSort] = useState<{ key: SortKey; dir: 'asc' | 'desc' }>({ key: 'spend', dir: 'desc' });

  const rows = useMemo(() => {
    const visible = campaigns.filter((c) => status === 'all' || (status === 'active' ? isActive(c.status) : isPaused(c.status)));
    const dir = sort.dir === 'asc' ? 1 : -1;
    return [...visible].sort((a, b) => {
      if (sort.key === 'name') return a.name.localeCompare(b.name) * dir;
      const av = a[sort.key] ?? -Infinity;
      const bv = b[sort.key] ?? -Infinity;
      return ((av as number) - (bv as number)) * dir;
    });
  }, [campaigns, status, sort]);

  const maxSpend = Math.max(1, ...rows.map((r) => r.spend));
  const showRoas = rows.some((r) => r.roas !== null);
  const columns = COLUMNS.filter((c) => c.key !== 'roas' || showRoas);

  const exportCsv = () => {
    // downloadCsv writes it through toCsv with a UTF-8 BOM and revokes the URL after the click.
    downloadCsv(campaignCsvRows(rows), `${title.toLowerCase().replace(/\s+/g, '-')}-campaigns-${range}.csv`);
  };

  return (
    <Card noPadding className={className}>
      <div className="flex items-center justify-between gap-2 px-5 pb-3 pt-4">
        <div>
          <h3 className="text-sm font-semibold text-slate-900">Campaigns</h3>
          <p className="text-xs text-slate-500">Click a campaign to filter the whole dashboard by it</p>
        </div>
        <Button size="sm" variant="ghost" onClick={exportCsv} disabled={!rows.length}>
          <Download className="mr-1 h-4 w-4" /> CSV
        </Button>
      </div>
      {rows.length ? (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-y border-slate-100 bg-slate-50/70 text-2xs uppercase tracking-wide text-slate-500">
                {columns.map((c) => (
                  <th
                    key={c.key}
                    className={cn('cursor-pointer select-none whitespace-nowrap px-3 py-2 font-semibold first:pl-5 last:pr-5', c.align === 'right' ? 'text-right' : 'text-left')}
                    onClick={() => setSort((s) => ({ key: c.key, dir: s.key === c.key && s.dir === 'desc' ? 'asc' : 'desc' }))}
                    aria-sort={sort.key === c.key ? (sort.dir === 'asc' ? 'ascending' : 'descending') : undefined}
                  >
                    <span className="inline-flex items-center gap-1">
                      {c.label}
                      {sort.key === c.key && (sort.dir === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />)}
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const selected = selectedIds.includes(r.id);
                const dimmed = selectedIds.length > 0 && !selected;
                return (
                  <tr
                    key={r.id}
                    onClick={() => onToggle(r.id)}
                    className={cn(
                      'cursor-pointer border-b border-slate-100 transition-colors last:border-0 hover:bg-slate-50',
                      selected && 'bg-blue-50/60 hover:bg-blue-50',
                      dimmed && 'opacity-55',
                    )}
                  >
                    <td className="max-w-[18rem] py-2.5 pl-5 pr-3">
                      <div className="flex items-center gap-2">
                        <input type="checkbox" readOnly checked={selected} className="h-3.5 w-3.5 rounded border-slate-300" aria-label={`Filter by ${r.name}`} />
                        <div className="min-w-0">
                          <p className="truncate font-medium text-slate-900" title={r.name}>{r.name}</p>
                          <div className="mt-0.5 flex items-center gap-1.5">
                            {statusBadge(r.status)}
                            {r.type && <span className="text-2xs text-slate-400">{r.type}</span>}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-3 py-2.5 text-right">
                      <div className="tabular-nums font-medium text-slate-900">{money(r.spend, currency)}</div>
                      <div className="ml-auto mt-1 h-1 w-20 rounded-full bg-slate-100">
                        <div className="h-1 rounded-full bg-blue-500" style={{ width: `${(r.spend / maxSpend) * 100}%` }} />
                      </div>
                    </td>
                    <td className="px-3 py-2.5 text-right tabular-nums text-slate-700">{num(r.impressions)}</td>
                    <td className="px-3 py-2.5 text-right tabular-nums text-slate-700">{num(r.clicks)}</td>
                    <td className="px-3 py-2.5 text-right tabular-nums text-slate-700">{pct(r.ctr)}</td>
                    <td className="px-3 py-2.5 text-right tabular-nums text-slate-700">{money(r.cpc, currency, 2)}</td>
                    <td className="px-3 py-2.5 text-right tabular-nums font-medium text-slate-900">{num(r.conversions, r.conversions % 1 ? 1 : 0)}</td>
                    <td className="px-3 py-2.5 text-right tabular-nums text-slate-700">{money(r.cpa, currency)}</td>
                    {showRoas && <td className="py-2.5 pl-3 pr-5 text-right tabular-nums text-slate-700">{r.roas !== null ? `${r.roas.toFixed(2)}x` : '—'}</td>}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="px-5 pb-8 pt-4 text-center text-sm text-slate-400">No campaigns with activity in this range</div>
      )}
    </Card>
  );
}

/**
 * The campaign table as CSV rows. Text cells come from the ad platform (a
 * campaign name is whatever the advertiser typed), so they are defused: a name
 * starting with = + - @ would otherwise run as a formula in Excel. Numbers are
 * ours and stay as they are.
 */
export function campaignCsvRows(rows: AdsCampaignRow[]): string[][] {
  const text = (v: string | null | undefined) => defuseFormula(v ?? '');
  const n = (v: number | null | undefined) => (v === null || v === undefined ? '' : String(v));
  return [
    ['Campaign', 'Status', 'Type', 'Spend', 'Impressions', 'Clicks', 'CTR %', 'CPC', 'Conversions', 'Cost per conversion', 'Conversion value', 'ROAS'],
    ...rows.map((r) => [
      text(r.name), text(r.status), text(r.type),
      n(r.spend), n(r.impressions), n(r.clicks), n(r.ctr), n(r.cpc), n(r.conversions), n(r.cpa), n(r.conversionValue), n(r.roas),
    ]),
  ];
}

// ─── Conversions by type ──────────────────────────────────────────────────────

function ConversionTypesCard({ rows, currency, color }: { rows: AdsReport['conversionsByType'] & object; currency: string | null; color: string }) {
  const max = Math.max(1, ...rows.map((r) => r.count));
  return (
    <Card>
      <h3 className="text-sm font-semibold text-slate-900">Conversions by type</h3>
      <p className="mb-4 text-xs text-slate-500">What the ads produced</p>
      {rows.length ? (
        <ul className="space-y-3">
          {rows.map((r) => (
            <li key={r.label}>
              <div className="mb-1 flex items-baseline justify-between gap-2 text-xs">
                <span className="truncate font-medium text-slate-700" title={r.label}>{r.label}</span>
                <span className="whitespace-nowrap tabular-nums text-slate-900">
                  {num(r.count, r.count % 1 ? 1 : 0)}
                  {r.value > 0 && <span className="ml-1.5 text-slate-400">{money(r.value, currency)}</span>}
                </span>
              </div>
              <div className="h-2 rounded-full bg-slate-100">
                <div className="h-2 rounded-full" style={{ width: `${(r.count / max) * 100}%`, background: color }} />
              </div>
            </li>
          ))}
        </ul>
      ) : (
        <div className="flex h-32 items-center justify-center text-center text-sm text-slate-400">No conversions in this range</div>
      )}
    </Card>
  );
}
